import ReportSectionCard from './shared/ReportSectionCard.component'
import ReportSectionHeading from '@components/pages/Reports/shared/ReportSectionHeading.component'
import React, { useMemo, useState } from 'react'
import ReportViewTabs from '@components/pages/Reports/shared/ReportViewTabs.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import ReportBundleStickyTopWhenExpanded from '@components/pages/Reports/shared/ReportBundleStickyTopWhenExpanded.component'
import ReportFilterToolbar from '@components/pages/Reports/shared/ReportFilterToolbar.component'
import ReportKpiGrid from '@components/pages/Reports/shared/ReportKpiGrid.component'
import ReportPageHeader from '@components/pages/Reports/shared/ReportPageHeader.component'
import { REPORTS_DISTRIBUTION_VIEW_TABS } from '@constants/Reports/patientBaseHealth.constants'
import {
  CLAIMS_PERFORMANCE_FILTERS,
  CLAIMS_PERFORMANCE_KPI_DEFINITIONS,
} from '@constants/Reports/claimsPerformance.constants'
import {
  ReportsDistributionView,
  ReportsKpiMetric,
} from '@dataTypes/reports.type'
import { useClaimsPerformanceFinance } from '@hooks/reports/useClaimsPerformanceFinance'
import { useReportFinanceUiEffects } from '@hooks/reports/useReportFinanceUiEffects'
import {
  hasNonZeroDashboardValues,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import {
  computeDynamicDollarAxis,
  formatKpiPercent,
} from '@utils/reports/revenueRealizationFinance.util'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from '@components/pages/Reports/shared/ReportDesignSystemCharts.component'

const CLAIMS_STATUS_COLORS: Record<string, string> = {
  Submitted: 'var(--vivid-green-300)',
  Pending: 'var(--blue-400)',
  Overdue: 'var(--orange-300)',
  Denied: 'var(--red-400)',
}

const REPORT_CHART_AXIS_TICK = { fontSize: 12, fill: 'var(--gray-500)' }
const REPORT_CHART_TOOLTIP_STYLE = {
  borderRadius: 8,
  border: '1px solid var(--gray-200)',
  fontSize: 12,
}
const REPORT_CHART_GRID_STROKE = 'var(--gray-100)'
const REPORT_BAR_CHART_MARGIN = { top: 24, right: 12, left: 4, bottom: 0 }
const REPORT_LINE_CHART_MARGIN = { top: 16, right: 12, left: 4, bottom: 8 }
const REPORT_BAR_CATEGORY_GAP = '16%'
const REPORT_YAXIS_WIDTH = 52

function formatDollarK(value: number): string {
  if (value === 0) return '$0'
  const k = value / 1000
  return Number.isInteger(k) ? `$${k}K` : `$${k.toFixed(1)}K`
}

function formatDollarTooltip(value: number): string {
  return `$${value.toLocaleString('en-US')}`
}

function formatCountLabel(value: number): string {
  if (value >= 1000) {
    const k = value / 1000
    return Number.isInteger(k) ? `${k}K` : `${k.toFixed(1)}K`
  }
  return value.toLocaleString('en-US')
}

function formatKpiCount(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return '—'
  return Math.round(value).toLocaleString('en-US')
}

function computeDaysAxis(values: number[]): {
  domain: [number, number]
  ticks: number[]
} {
  const max = values.length ? Math.max(...values, 0) : 0
  const axisMax = max <= 0 ? 40 : Math.ceil((max * 1.15) / 5) * 5
  const min =
    values.length > 0 ? Math.max(0, Math.floor(Math.min(...values) * 0.9)) : 0
  const axisMin = max <= 0 ? 0 : Math.min(min, Math.floor(axisMax * 0.5))
  const step = axisMax - axisMin <= 20 ? 5 : axisMax - axisMin <= 40 ? 10 : 25
  const ticks: number[] = []
  for (let t = axisMin; t <= axisMax; t += step) ticks.push(t)
  if (ticks[ticks.length - 1] !== axisMax) ticks.push(axisMax)
  return { domain: [axisMin, axisMax], ticks }
}

function computeCountAxis(values: number[]): {
  domain: [number, number]
  ticks: number[]
  tickFormatter: (v: number) => string
} {
  const max = values.length ? Math.max(...values, 0) : 0
  const axisMax = max <= 0 ? 10 : Math.ceil((max * 1.15) / 5) * 5 || 10
  const step = axisMax <= 20 ? 5 : axisMax <= 100 ? 25 : Math.ceil(axisMax / 4)
  const ticks: number[] = []
  for (let t = 0; t <= axisMax; t += step) ticks.push(t)
  if (ticks[ticks.length - 1] !== axisMax) ticks.push(axisMax)
  return { domain: [0, axisMax], ticks, tickFormatter: formatCountLabel }
}

function stripAgingDaysSuffix(label: string): string {
  return label.replace(/\s*days$/i, '').trim()
}

function ClaimsPerformanceReport(): React.ReactElement {
  const [statusView, setStatusView] = useState<ReportsDistributionView>('Count')
  const [agingView, setAgingView] = useState<ReportsDistributionView>('Count')
  const [reportSaveViewAction, setReportSaveViewAction] =
    useState<JSX.Element | null>(null)
  const [reportGeneratePDFAction, setReportGeneratePDFAction] =
    useState<JSX.Element | null>(null)
  // `reportFiltersAndMetrics` is the source of truth for the filters and metrics of the currently selected report view.
  // Use this for all data queries and any logic that depends on the selected report view.
  const [reportFiltersAndMetrics, setReportFiltersAndMetrics] = useState<{
    filters: Record<string, boolean>
    metrics: Record<string, boolean>
  } | null>(null)
  const { loading, error, data } = useClaimsPerformanceFinance(
    (reportFiltersAndMetrics?.filters ?? {}) as unknown as Record<
      string,
      string
    >,
  )
  useReportFinanceUiEffects({
    loading,
    error,
    errorMessage: 'Failed to load claims performance data. Please try again.',
  })

  const kpiItems = useMemo((): ReportsKpiMetric[] => {
    if (!data) {
      return CLAIMS_PERFORMANCE_KPI_DEFINITIONS.map((def) => ({
        id: def.id,
        title: def.title,
        footer: def.footer,
        value: loading ? '…' : '—',
      }))
    }

    const valueById: Record<string, number> = {
      'total-claims-submitted': data.totalClaimsSubmitted.value,
      'claims-pending': data.claimsPending.value,
      'claims-overdue': data.claimsOverdue.value,
      'claims-denied': data.claimsDenied.value,
      'claims-acceptance-rate': data.claimsAcceptanceRate.value,
      'resubmission-rate': data.resubmissionRate.value,
    }

    return CLAIMS_PERFORMANCE_KPI_DEFINITIONS.map((def) => ({
      id: def.id,
      title: def.title,
      footer: def.footer,
      value:
        def.format === 'percent'
          ? formatKpiPercent(valueById[def.id])
          : formatKpiCount(valueById[def.id]),
    }))
  }, [data, loading])

  const paymentTrendData = useMemo(() => {
    if (!data) return []
    return data.averageDaysToPaymentByPeriod.buckets.map((row) => ({
      month: row.label,
      days: row.value,
    }))
  }, [data])

  const paymentTrendAxis = useMemo(
    () => computeDaysAxis(paymentTrendData.map((r) => r.days)),
    [paymentTrendData],
  )

  const statusChartData = useMemo(() => {
    if (!data) return []
    const rows = data.claimsStatusOverview.buckets
    if (statusView === 'Count') {
      return rows.map((row) => ({
        label: row.label,
        barValue: row.value,
        topLabel: formatCountLabel(row.value),
      }))
    }
    const total = rows.reduce((sum, r) => sum + r.value, 0) || 1
    return rows.map((row) => {
      const pct = Math.round((row.value / total) * 1000) / 10
      return {
        label: row.label,
        barValue: pct,
        topLabel: `${pct}%`,
      }
    })
  }, [data, statusView])

  const statusYAxis = useMemo(() => {
    if (statusView === 'Percentage') {
      return {
        domain: [0, 100] as [number, number],
        ticks: [0, 20, 40, 60, 80, 100],
        tickFormatter: (v: number) => `${v}%`,
      }
    }
    return computeCountAxis(statusChartData.map((r) => r.barValue))
  }, [statusView, statusChartData])

  const carrierData = useMemo(() => {
    if (!data) return []
    return data.averageDaysToPaymentByCarrier.buckets.map((row) => ({
      carrier: row.label,
      barValue: row.value,
      topLabel: `${row.value.toFixed(1)}`,
    }))
  }, [data])

  const carrierAxis = useMemo(
    () => computeDaysAxis(carrierData.map((r) => r.barValue)),
    [carrierData],
  )

  const agingChartData = useMemo(() => {
    if (!data) return []
    const rows = data.insuranceArAgingBuckets.buckets.map((row) => ({
      label: stripAgingDaysSuffix(row.label),
      amount: row.value,
    }))
    if (agingView === 'Count') {
      return rows.map((bucket) => ({
        label: bucket.label,
        barValue: bucket.amount,
        topLabel: formatDollarK(bucket.amount),
      }))
    }
    const total = rows.reduce((sum, b) => sum + b.amount, 0) || 1
    return rows.map((bucket) => {
      const pct = Math.round((bucket.amount / total) * 1000) / 10
      return {
        label: bucket.label,
        barValue: pct,
        topLabel: `${pct}%`,
      }
    })
  }, [data, agingView])

  const agingYAxis = useMemo(() => {
    if (agingView === 'Percentage') {
      return {
        domain: [0, 100] as [number, number],
        ticks: [0, 20, 40, 60, 80, 100],
        tickFormatter: (v: number) => `${v}%`,
      }
    }
    return computeDynamicDollarAxis(agingChartData.map((r) => r.barValue))
  }, [agingView, agingChartData])

  const kpiEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      data?.totalClaimsSubmitted.value,
      data?.claimsPending.value,
      data?.claimsOverdue.value,
      data?.claimsDenied.value,
      data?.claimsAcceptanceRate.value,
      data?.resubmissionRate.value,
    ),
  )
  const paymentTrendEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.averageDaysToPaymentByPeriod?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const statusEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.claimsStatusOverview?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const carrierEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.averageDaysToPaymentByCarrier?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const agingEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.insuranceArAgingBuckets?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )

  return (
    <div className="reports-patient-base-health reports-treatment-conversion-funnel reports-claims-performance">
      <ReportBundleStickyTopWhenExpanded>
        <ReportPageHeader title="Claims Performance">
          {reportSaveViewAction}
          {reportGeneratePDFAction}
        </ReportPageHeader>

        <ReportFilterToolbar
          filters={CLAIMS_PERFORMANCE_FILTERS}
          onReportSaveViewActionChange={setReportSaveViewAction}
          onReportGeneratePDFActionChange={setReportGeneratePDFAction}
          onReportFiltersAndMetricsChange={setReportFiltersAndMetrics}
        />
      </ReportBundleStickyTopWhenExpanded>

      {kpiEmpty ? (
        <DashboardSectionEmptyState />
      ) : (
        <ReportKpiGrid items={kpiItems} />
      )}

      <div className="reports-treatment-conversion-funnel__stack">
        <div className="reports-treatment-conversion-funnel__grid reports-claims-performance__top-grid">
          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Average Days to Payment Trend
                </ReportSectionHeading>
              </div>
              {paymentTrendEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={paymentTrendData}
                      margin={REPORT_LINE_CHART_MARGIN}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        domain={paymentTrendAxis.domain}
                        ticks={paymentTrendAxis.ticks}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip
                        formatter={(value: number) => `${value} days`}
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Line
                        type="linear"
                        dataKey="days"
                        stroke="var(--blue-500)"
                        strokeWidth={2}
                        dot={false}
                        activeDot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </ReportSectionCard>

          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Claims Status Overview
                </ReportSectionHeading>
                {statusEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={statusView}
                    onChange={(value) =>
                      setStatusView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {statusEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={statusChartData}
                      margin={REPORT_BAR_CHART_MARGIN}
                      barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="label"
                        axisLine={false}
                        tickLine={false}
                        interval={0}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        domain={statusYAxis.domain}
                        ticks={statusYAxis.ticks}
                        tickFormatter={statusYAxis.tickFormatter}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip
                        formatter={(value: number) =>
                          statusView === 'Count'
                            ? value.toLocaleString('en-US')
                            : `${value}%`
                        }
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Bar
                        dataKey="barValue"
                        radius={[8, 8, 8, 8]}
                        barSize={100}
                      >
                        {statusChartData.map((entry) => (
                          <Cell
                            key={entry.label}
                            fill={
                              CLAIMS_STATUS_COLORS[entry.label] ||
                              'var(--blue-400)'
                            }
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </ReportSectionCard>
        </div>

        <div className="reports-treatment-conversion-funnel__grid reports-claims-performance__bottom-grid">
          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Average Days to Payment by Carrier
                </ReportSectionHeading>
              </div>
              {carrierEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={carrierData}
                      margin={REPORT_BAR_CHART_MARGIN}
                      barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="carrier"
                        axisLine={false}
                        tickLine={false}
                        interval={0}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        domain={carrierAxis.domain}
                        ticks={carrierAxis.ticks}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip
                        formatter={(value: number) => `${value} days`}
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Bar
                        dataKey="barValue"
                        fill="var(--blue-400)"
                        radius={[8, 8, 8, 8]}
                        barSize={100}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </ReportSectionCard>

          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Insurance Aging Breakdown
                </ReportSectionHeading>
                {agingEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={agingView}
                    onChange={(value) =>
                      setAgingView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {agingEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={agingChartData}
                      margin={REPORT_BAR_CHART_MARGIN}
                      barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="label"
                        axisLine={false}
                        tickLine={false}
                        interval={0}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        domain={agingYAxis.domain}
                        ticks={agingYAxis.ticks}
                        tickFormatter={agingYAxis.tickFormatter}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip
                        formatter={(value: number) =>
                          agingView === 'Count'
                            ? formatDollarTooltip(value)
                            : `${value}%`
                        }
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Bar
                        dataKey="barValue"
                        fill="var(--blue-400)"
                        radius={[8, 8, 8, 8]}
                        barSize={100}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </ReportSectionCard>
        </div>
      </div>
    </div>
  )
}

export default ClaimsPerformanceReport
